import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { SkeletonLoader } from '../../external-dependencies/components/SkeletonLoader/SkeletonLoader';

interface SkeletonLoaderProps {
  loading: boolean;
  size?: string;
  delayInMS?: number;
  children?: ReactNode;
}

/**
 * Wrapper component that renders a skeleton loading UI when loading parameter is true or Children, otherwise.
 * @param SkeletonLoaderProps object:
 *  loading: Sets the returned component. If true, it returns a Skeleton loading component. If false, it returns `Children`
 *  size: Sets the size of the Skeleton loading component
 *  delayInMS: If set, the Skeleton loading component only appears after the provided delay in ms
 * @returns Skeleton Loader component if `loading` is true or `Children` if `loading` is false
 */
export const LoaderWrapper = ({
  loading,
  delayInMS = 0,
  size,
  children,
}: SkeletonLoaderProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(loading);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (loading === true && delayInMS > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsLoading(true);
      }, delayInMS);
    } else {
      setIsLoading(loading);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delayInMS, loading]);

  if (isLoading) {
    return (
      <SkeletonLoader.Line
        style={{ width: size, flexGrow: 1, margin: 0 }}
        size="16px"
        data-testid="loader-wrapper__skeleton"
      />
    );
  }

  return <>{children}</>;
};
